/**
 * RealtimeSignalingService - Bridges the Realtime API message format
 * to/from the PeerCoordinator's ISignalingService interface.
 *
 * Outbound: PeerCoordinator calls sendOffer/sendAnswer/sendIceCandidate
 *           -> this service formats and sends via the realtime websocket.
 *
 * Inbound:  peer.ts handler calls handleIncomingMessage(PeerMessage)
 *           -> this service converts to SignalingMessage and notifies PeerCoordinator.
 */

import { PeerMessage, PeerMessageType, websocket } from '@playcastdotio/Messaging';
import type { PeerMetadata, SignalingGenerationV1 } from '@playcastdotio/Messaging';
import type { ISignalingService, SignalingMessage, PeerIdAssignedData } from '../../coordinators/peer/types';
import { sendMessage } from '../../api/modules/realtime';
import { HostSignalingGenerationManager } from './HostSignalingGenerationManager';

export class RealtimeSignalingService implements ISignalingService {
  private signalingHandlers: Array<(msg: SignalingMessage) => void> = [];
  private peerIdAssignedHandlers: Array<(data: PeerIdAssignedData) => void> = [];

  constructor(private readonly generations = new HostSignalingGenerationManager()) {}

  sendOffer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit,
    connectionGeneration?: SignalingGenerationV1): void {
    if (!this.generations.rememberLocalOffer(targetPeerId, connectionGeneration)) return;
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_SDP_OFFER, {
        fromId: peerId,
        toId: targetPeerId,
        sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' },
        connectionGeneration,
      }),
    );
  }

  sendAnswer(peerId: string, targetPeerId: string, sdp: RTCSessionDescriptionInit,
    connectionGeneration?: SignalingGenerationV1): void {
    // Only echo to a peer PROVEN to speak generations. Falling back to the caller's
    // `connectionGeneration` for a legacy peer asserts capability on the host's behalf, and
    // a generation-aware peer would then reject the host's later signals.
    const generation = this.generationForOutbound(targetPeerId, connectionGeneration);
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_SDP_ANSWER, {
        fromId: peerId,
        toId: targetPeerId,
        sdp: { type: sdp.type as 'offer' | 'answer', sdp: sdp.sdp ?? '' },
        connectionGeneration: generation,
      }),
    );
  }

  sendIceCandidate(peerId: string, targetPeerId: string, candidates: RTCIceCandidateInit[],
    connectionGeneration?: SignalingGenerationV1): void {
    const json = candidates.map((c) => JSON.stringify(c));
    sendMessage(
      websocket.generators.peer(PeerMessageType.PEER_ICE_CANDIDATE, {
        fromId: peerId,
        toId: targetPeerId,
        candidates: { json },
        connectionGeneration: this.generationForOutbound(targetPeerId, connectionGeneration),
      }),
    );
  }

  /**
   * The generation to send to `targetPeerId`: the peer's own echoed generation when the peer
   * proved it speaks generations, otherwise undefined. Never the host's own envelope.
   */
  private generationForOutbound(
    targetPeerId: string,
    connectionGeneration?: SignalingGenerationV1,
  ): SignalingGenerationV1 | undefined {
    const echoed = this.generations.echoFor(targetPeerId);
    if (echoed) return echoed;
    return this.generations.requiresGeneration(targetPeerId) ? connectionGeneration : undefined;
  }

  public clearPeerGeneration(peerId: string): void {
    this.generations.clear(peerId);
  }

  public clearAllPeerGenerations(): void {
    this.generations.clearAll();
  }

  requestPeerId(_type: string, _metadata: PeerMetadata): void {
    // Not needed: SessionCoordinator handles peer ID requests via the legacy path.
  }

  onPeerIdAssigned(handler: (data: PeerIdAssignedData) => void): () => void {
    this.peerIdAssignedHandlers.push(handler);
    return () => {
      const idx = this.peerIdAssignedHandlers.indexOf(handler);
      if (idx > -1) this.peerIdAssignedHandlers.splice(idx, 1);
    };
  }

  onSignalingMessage(handler: (msg: SignalingMessage) => void): () => void {
    this.signalingHandlers.push(handler);
    return () => {
      const idx = this.signalingHandlers.indexOf(handler);
      if (idx > -1) this.signalingHandlers.splice(idx, 1);
    };
  }

  /**
   * Called by the peer.ts handler to route incoming PeerMessages
   * into the PeerCoordinator via the registered signaling/peerIdAssigned handlers.
   */
  handleIncomingMessage(message: PeerMessage): void {
    if (message.type === PeerMessageType.PEER_GET_ID_ANSWER) {
      const data: PeerIdAssignedData = {
        peerId: message.peerId,
        peerIdType: message.peerIdType,
        iceServers: message.iceServers ? [message.iceServers] : [],
      };
      for (const handler of this.peerIdAssignedHandlers) {
        handler(data);
      }
      return;
    }

    if (!this.validateIncomingMessage(message)) return;
    const signalingMessage = this.convertToSignalingMessage(message);
    if (!signalingMessage) return;

    for (const handler of this.signalingHandlers) {
      handler(signalingMessage);
    }
  }

  /**
   * Frozen `api/` handlers must not own generation policy. They call this so validation
   * stays on the signaling service; on accept, `forward` runs the legacy route.
   */
  forwardValidated(message: PeerMessage, forward: (message: PeerMessage) => void): void {
    if (!this.validateIncomingMessage(message)) return;
    forward(message);
  }

  /** Validate before any coordinator or legacy WebRTC object observes the signal. */
  validateIncomingMessage(message: PeerMessage): boolean {
    const kind = message.type === PeerMessageType.PEER_SDP_OFFER
      ? 'offer'
      : message.type === PeerMessageType.PEER_SDP_ANSWER
        ? 'answer'
        : message.type === PeerMessageType.PEER_ICE_CANDIDATE
          ? 'ice-candidate'
          : undefined;
    if (!kind || !('fromId' in message)) return true;
    const accepted = this.generations.acceptInbound(
      message.fromId,
      kind,
      message.connectionGeneration,
      message.type === PeerMessageType.PEER_SDP_OFFER ? message.peerMetadata : undefined,
    );
    if (!accepted) {
      // A dropped signal used to be a silent no-op: the guest burned its reconnect budget
      // against a host that had stopped listening. Make it observable. Never log sessionId.
      console.warn('[RealtimeSignalingService] Dropped inbound signal rejected by generation guard', {
        kind,
        peerId: message.fromId,
        hasGeneration: message.connectionGeneration !== undefined,
      });
    }
    return accepted;
  }

  private convertToSignalingMessage(message: PeerMessage): SignalingMessage | null {
    switch (message.type) {
      case PeerMessageType.PEER_SDP_OFFER:
        return {
          type: 'offer',
          fromId: message.fromId,
          toId: message.toId,
          sdp: message.sdp,
          peerMetadata: message.peerMetadata,
          peerIdType: message.peerIdType,
          connectionGeneration: message.connectionGeneration,
        };

      case PeerMessageType.PEER_SDP_ANSWER:
        return {
          type: 'answer',
          fromId: message.fromId,
          toId: message.toId,
          sdp: message.sdp,
          connectionGeneration: message.connectionGeneration,
        };

      case PeerMessageType.PEER_ICE_CANDIDATE: {
        const jsonCandidates = message.candidates.json;
        const candidates: RTCIceCandidateInit[] = [];
        for (const candidateJson of jsonCandidates) {
          try {
            candidates.push(JSON.parse(candidateJson) as RTCIceCandidateInit);
          } catch {
            console.warn('[RealtimeSignalingService] Failed to parse ICE candidate');
          }
        }
        return {
          type: 'ice-candidate',
          fromId: message.fromId,
          toId: message.toId,
          candidates,
          connectionGeneration: message.connectionGeneration,
        };
      }

      default:
        return null;
    }
  }
}
